/**
 * The shared time-range control. Preset windows, computed at click time.
 *
 * "All time" is deliberately absent: the server bounds a query at 366 days
 * (`MAX_RANGE_DAYS`), and offering a button the server will refuse teaches
 * users that buttons lie.
 */

import type { RangeParams } from '../api/types'

const PRESETS: ReadonlyArray<{ label: string; days: number | null }> = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: null }, // the server's default window
  { label: '90 days', days: 90 },
  { label: '365 days', days: 365 },
]

function daysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString()
}

export function RangePicker(props: {
  value: RangeParams
  onChange: (next: RangeParams) => void
}) {
  const activeDays = props.value.from ? spanOf(props.value) : null

  return (
    <div className="range-picker" role="group" aria-label="Time range">
      {PRESETS.map((preset) => {
        const active = preset.days === activeDays
        return (
          <button
            key={preset.label}
            type="button"
            aria-pressed={active}
            onClick={() =>
              props.onChange(preset.days === null ? {} : { from: daysAgo(preset.days) })
            }
          >
            {preset.label}
          </button>
        )
      })}
    </div>
  )
}

/** Which preset a stored `from` corresponds to, for the pressed state. */
function spanOf(range: RangeParams): number | null {
  if (!range.from) return null
  const ms = Date.now() - new Date(range.from).getTime()
  const days = Math.round(ms / 86_400_000)
  return [7, 90, 365].find((d) => Math.abs(d - days) <= 1) ?? null
}
